import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";

import { useChatSessionStateStore, useSocketStateStore } from "@asurion-core/headless-chat-web";
import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useNotification } from "@expert/common-ui";
import { useEffect, useMemo } from "react";

import type { OpenGuideSource } from "../types";

import { GuidedFlowMessageMapper, type LogicBuilderGlobalConfig, useSimplrInitializers } from "../logic-builder";
import { useGuideSession, useGuideStore } from "../store";
import { useGuideDisclosure } from "./useGuideDisclosure";
import { useParseMessages } from "./useParseMessages";

export interface GuideManagerProps {
    // Session/task
    sessionId: string;
    callSid?: string;
    employeeId?: string;
    isCustomerSession?: boolean;

    // Simplr config
    globalConfig?: LogicBuilderGlobalConfig;

    // Other
    analytics?: Record<string, unknown>;
    clientChannelId?: string;
    logger: Logger;
    partner: Partner;
    sendMessage: SendExpertAssistMessage;
    ssoAccessToken: string;
    taskMdn: string;
}

interface GuideToolboxCommand {
    open: (source: OpenGuideSource) => void;
    tooltip?: string;
    quickActionLabel?: string;
    disabled: boolean;
}

export function useGuideManager({
    callSid,
    clientChannelId,
    employeeId,
    globalConfig,
    logger: loggerProp,
    partner,
    sendMessage,
    sessionId,
    ssoAccessToken,
    taskMdn,
}: GuideManagerProps) {
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(() => loggerProp.child({ module: "useGuideManager" }), [loggerProp]);

    useEffect(() => {
        logger.info({ sessionId, platformTicketId }, "Solve Guide | Session Updated");
    }, [sessionId, platformTicketId, logger]);

    useSimplrInitializers({
        globalConfig,
        logger,
        sessionId,
    });

    const { connectionState } = useSocketStateStore();
    const notification = useNotification();

    const {
        analyticsInfo: { currentStepId },
        availableGuides,
        isLoadingSimplrSession,
    } = useGuideSession(sessionId);
    const areGuidesAvailable = availableGuides.length > 0;

    const { latestMessage, messages } = useParseMessages({ employeeId, sessionId });
    const { isGuideOpen, openGuide, closeGuide } = useGuideDisclosure({
        callSid,
        logger,
        platformTicketId,
        sessionId,
    });

    useEffect(() => {
        const { setIsErrorState, setErrorStateSessionId, getGuideSession } = useGuideStore.getState();
        if (connectionState === "ticketEnded") {
            logger.info("Solve Guide | Ticket Ended event received");
            setIsErrorState(sessionId, true);
            // Only seed once per error — chat-switch re-fires would otherwise keep appending `_RESTARTED`.
            if (!getGuideSession(sessionId).errorStateSessionId) {
                setErrorStateSessionId(sessionId);
            }
        } else setIsErrorState(sessionId, false);
    }, [connectionState, sessionId, logger]);

    useEffect(() => {
        if (platformTicketId && currentStepId !== "" && isLoadingSimplrSession === true) {
            logger.info(
                { currentStepId, platformTicketId },
                "Solve Guide | platformTicketId updated and first step loaded, turning off loading state",
            );
            useGuideStore.getState().setIsLoadingSimplrSession(sessionId, false);
        }
    }, [currentStepId, isLoadingSimplrSession, platformTicketId, sessionId, logger]);

    const ActiveGuide = areGuidesAvailable ? (
        <GuidedFlowMessageMapper
            callSid={callSid}
            clientChannelId={clientChannelId}
            latestMessage={latestMessage}
            logger={logger}
            partner={partner}
            onGuideClose={closeGuide}
            opened={isGuideOpen}
            sendMessage={sendMessage}
            sessionId={sessionId}
            ssoAccessToken={ssoAccessToken}
            taskMdn={taskMdn}
        />
    ) : null;

    const handleOpenGuideFailure = (source: OpenGuideSource) => {
        void sendErrorMetric(`openGuideFailure`);
        // TODO: @asurion-core/expert-workspace-solve: we should not see this error
        logger.warn(
            { source, connectionState, globalConfig, messages: JSON.stringify(messages) },
            "Solve guide | No active guide found, trigger error toast",
        );
        notification.error("We're having trouble launching the guide—try again.");
    };

    return {
        areGuidesAvailable,
        guide: ActiveGuide,
        guideToolboxCommand: {
            open: ActiveGuide ? openGuide : handleOpenGuideFailure,
            tooltip: !areGuidesAvailable ? "Coming soon" : undefined,
            quickActionLabel: areGuidesAvailable ? "/g" : undefined,
            disabled: !areGuidesAvailable,
        } as GuideToolboxCommand,
        isGuideOpen,
        closeGuide,
        openGuide,
    };
}
